import React, { useState, useContext } from "react";
import { AccountContext } from "../../store/AccountContext";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
  formHelperTextClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { Link, Navigate } from "react-router-dom";
import { v4GridContainerWidthSx } from "../../theme/v4CompatSx";
import { useToasts } from "react-toast-notifications";

import { Field, Form } from "react-final-form";

import {
  composeValidators,
  isEmail,
  isEmailRequired,
} from "../../utils/validators";
import { DuplicateEmailError } from "../../api/errors";
import { moveEmail } from "../../api/client";
import CodeForm from "../common/CodeForm";

/**
 * PM5 T2 (money path, batch "myaccount-profile") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **AUTH ESCALATION CLASS.** Changing the email address changes the login
 * identifier, and the duplicate-email path moves an address between accounts
 * behind a 6-digit code. The conversion is styling-only: no handler, validator,
 * state value, prop, condition or DOM node moved.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1): `background`, `myAccountContainer`,
 * `myAccountGridContainer` and `myAccountTitle` were declared but never
 * referenced from the JSX (copy-paste from MyAccount.js). `MuiInput` was
 * `{ input: {...} }` — a nested key with no `&`/`@`, which JSS's nested plugin
 * ignores, so it emitted no CSS at all.
 *
 * The `@ts-ignore`s on `theme.palette.background.*` are gone: `themeV5.ts`
 * declares those keys through a `declare module` augmentation, so they type.
 */

/* ---------------------------------------------------------------------- *
 * styled() — non-MUI elements that carry a fixed style
 * ---------------------------------------------------------------------- */

// was: useStyles().activeBreadcrumb — a react-router <Link>, which takes a
// `className` but has no `sx` prop (guide §1.2).
const ActiveBreadcrumbLink = styled(Link)(({ theme }) => ({
  color: theme.palette.background.ffNavy,
  "&:hover": {
    color: theme.palette.background.ffGreen,
  },
}));

/* ---------------------------------------------------------------------- *
 * CodeForm styling hooks
 * ---------------------------------------------------------------------- */

/**
 * `CodeForm` (converted at T1) styles its container / label / submit through
 * **`className` props**, and deliberately so: its v4 contract was
 * `className={override || classes.default}` — exactly one of the two applies,
 * never both (guide §2, "`className` fallback props"). Passing `sx` instead
 * would need a new prop on a shared component and would break that
 * exclusivity, so the three v4 `makeStyles` classes are re-expressed as stable
 * literal class names, styled from the wrapper Box's `sx` below.
 *
 * Same rendering, same DOM (no extra wrapper element), and CodeForm still sees
 * a truthy `submitClassName`/`textClassName`, so it keeps taking its
 * "caller has overridden me" branch exactly as it does today. Specificity goes
 * from (0,1,0) to (0,2,0), which is how it already won in v4 — there the user
 * JSS sheet simply had a later index than the library's.
 */
const CODE_FORM_CONTAINER_CLASS = "ff-change-email-code-form";
const CODE_FORM_TEXT_CLASS = "ff-change-email-code-text";
const CODE_FORM_SUBMIT_CLASS = "ff-change-email-code-submit";

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ v4's `<Grid container spacing={2}>` was
 *   `.MuiGrid-spacing-xs-2      { margin: -8px; width: calc(100% + 16px) }`
 *   `.MuiGrid-spacing-xs-2 > .MuiGrid-item { padding: 8px }`
 * v9 replaces that with `gap` on the container and no padding on the items
 * (guide §2). Carry v4's own declarations verbatim rather than re-deriving the
 * arithmetic: omit `spacing` (v9 default 0 ⇒ `gap: 0px`), and give each item
 * the 8px padding itself. Identical by construction.
 */
const v4GridContainerSx = { width: "calc(100% + 16px)", margin: "-8px" };
const v4GridItemSx = { padding: "8px" };

// was: useStyles().myAccountSubTitle. The literal `@media (min-width:600px)` /
// `(min-width:960px)` become `breakpoints.up("sm"|"md")`; `themeV5` pins those
// to the v4 values (600/960), so the emitted query is identical.
const myAccountSubTitleSx: SxProps<Theme> = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.4rem",
  textAlign: "left",
  // marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.4rem",
    lineHeight: "1.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.6rem",
    lineHeight: "1.8rem",
  },
});

// was: useStyles().bodyText
const bodyTextSx: SxProps<Theme> = (theme) => ({
  color: theme.palette.background.ffNavy,
});

// was: useStyles().bodyTextSml
const bodyTextSmlSx: SxProps<Theme> = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "0.9rem",
  "& span": {
    fontWeight: 500,
  },
});

// was: useStyles().formContainer
//
// PARITY RESTORE (guide §2, "`<Grid container>` lost its `width: 100%`"): this
// container's direct parent is another `<Grid container>`, so in v9 — where the
// Grid container emits no width rule at all — it becomes a flex item at
// `width: auto` and shrinks to fit its content. Measured on the change-email
// form: 718.66px → 245.41px.
const formContainerSx = (theme: Theme) => ({
  ...v4GridContainerWidthSx,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
});

// was: useStyles().inputField, merged with the Grid item padding above.
//
// §1.2b: `.MuiFormHelperText-root` reaches into the TextField this file
// renders. Both convert here, so the hand-written v4 string would stop matching
// under the `FfV5` prefix — the name is read off `formHelperTextClasses`
// instead. `& input` is an element selector, so it is unaffected.
const inputFieldItemSx = {
  padding: "8px",
  "& input": {
    padding: "12px",
    border: "1px solid #a5a5a5",
    backgroundColor: "#f7f7f7",
    margin: "4px 0 10px 0",
    fontSize: "16px",
    borderRadius: "5px",
  },
  [`& .${formHelperTextClasses.root}`]: {
    fontSize: "0.9rem",
    fontWeight: "500",
  },
};

// was: useStyles().footerDivider
const footerDividerSx: SxProps<Theme> = {
  height: "1px",
  backgroundColor: "#3c3b3b",
  margin: "5% 0 2% 0",
};

// was: useStyles().breadcrumbContainer
const breadcrumbContainerSx: SxProps<Theme> = (theme) => ({
  margin: theme.spacing(1, 0, 2, 0),
});

// was: useStyles().disabledBreadcrumb
const disabledBreadcrumbSx: SxProps<Theme> = { color: "#777777" };

// was: useStyles().submit, plus a v4 default that has to be restored.
//
// PARITY RESTORE (guide §2, "Button default color"): `color: "text.primary"` is
// NOT in the v4 rule. v4's plain <Button> defaulted to `color="default"`, whose
// root style was `color: theme.palette.text.primary`. v5+ deleted `default` and
// made `primary` the default, which would paint the label lime on the lime
// `typography.button` background — an invisible "Update Email" button.
const submitSx: SxProps<Theme> = {
  alignSelf: "flex-end",
  width: "fit-content",
  color: "text.primary",
};

// The Alert's "Move Email" action button carries no v4 rule of its own — only
// the same removed default colour, which still has to be restored.
const moveEmailSx: SxProps<Theme> = { color: "text.primary" };

// was: <Box m={5} display="flex" justifyContent="center" flexDirection="column">
// v9's Box reads `sx` only — the v4 shorthand system props are forwarded to the
// DOM and apply nothing (guide §2, "Box system props"). Values keep their
// meaning, so `m: 5` is still 40px.
const loadingBoxSx: SxProps<Theme> = {
  m: 5,
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
};

// was: <Box m={5} alignSelf="center">, plus the three v4 classes that were
// handed to CodeForm as `className` props (see the block above).
const codeFormBoxSx: SxProps<Theme> = (theme) => ({
  m: 5,
  alignSelf: "center",
  // was: useStyles().codeFormContainer
  [`& .${CODE_FORM_CONTAINER_CLASS}`]: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    justifyContent: "flex-end",
    marginTop: 0,
  },
  // was: useStyles().bodyText
  [`& .${CODE_FORM_TEXT_CLASS}`]: {
    color: theme.palette.background.ffNavy,
  },
  // was: useStyles().submit
  [`& .${CODE_FORM_SUBMIT_CLASS}`]: {
    alignSelf: "flex-end",
    width: "fit-content",
  },
});

// was: <Box display="flex" flex={1} justifyContent="flex-end" flexDirection="row">
const submitRowSx: SxProps<Theme> = {
  display: "flex",
  flex: 1,
  justifyContent: "flex-end",
  flexDirection: "row",
};

export default function ChangeEmail() {
  const { addToast } = useToasts();
  const [moving, setMoving] = useState<boolean>();
  const [movingError, setMovingError] = useState<string>();
  const [duplicateEmail, setDuplicateEmail] = useState<string>();
  const [duplicateEmailCodeSent, setDuplicateEmailCodeSent] =
    useState<boolean>(false);
  const [updated, setUpdated] = useState(false);

  // Account Context
  const { updating, userProfile, update, updateError, validateLogin } =
    useContext(AccountContext);

  // State
  const [emailNameSubmitted, setEmailSubmitted] = useState(false);

  const onSubmitEmail = async ({ email }: { email: string }) => {
    setEmailSubmitted(true);
    try {
      const updateEmail = await update({ email });
      if (updateEmail) {
        await validateLogin();
        addToast("Email successfully updated.", {
          appearance: "success",
        });
        setUpdated(true);
      }
    } catch (e) {
      if (e instanceof DuplicateEmailError) {
        setDuplicateEmail(email);
      }
    }
  };

  const initialiseMoveEmail = () => {
    if (moving || !duplicateEmail || duplicateEmailCodeSent) return;

    setMoving(true);
    setMovingError(undefined);

    moveEmail(duplicateEmail)
      .then(() => {
        setDuplicateEmailCodeSent(true);
      })
      .catch((e) => {
        setMovingError(e.toString());
      })
      .finally(() => setMoving(false));
  };

  const onSubmitVerificationCode = (verificationCode: string) => {
    if (moving || !duplicateEmail || !duplicateEmailCodeSent) return;

    setMoving(true);
    setMovingError(undefined);

    moveEmail(duplicateEmail, verificationCode)
      .then(({ moved }) => {
        if (!moved) {
          throw new Error("Invalid verification code");
        }

        return validateLogin().then(() => {
          addToast("Email successfully updated.", {
            appearance: "success",
          });
          setUpdated(true);
        });
      })
      .catch((e) => {
        setMovingError(e.toString());
      })
      .finally(() => setMoving(false));
  };

  if (updated) {
    return <Navigate to="/myaccount/overview" replace />;
  }

  if (!userProfile) {
    return null;
  }

  return (
    <Grid container>
      <Grid size={{ xs: 12, sm: 12 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={breadcrumbContainerSx}>
          <ActiveBreadcrumbLink to="/myaccount/overview">
            Overview
          </ActiveBreadcrumbLink>
          <Typography sx={disabledBreadcrumbSx}>
            Change Email Address
          </Typography>
        </Breadcrumbs>
      </Grid>
      <Grid size={{ xs: 12, sm: 12 }}>
        <Typography variant="h2" sx={myAccountSubTitleSx}>
          Change Email Address
        </Typography>

        {!duplicateEmailCodeSent && (
          <Typography variant="body1" sx={bodyTextSx}>
            Provide your new email address here.
          </Typography>
        )}
      </Grid>
      <Grid container sx={formContainerSx}>
        <Grid size={{ xs: 12, sm: 8, md: 7 }}>
          {updateError && emailNameSubmitted && (
            <Alert
              severity="error"
              variant="filled"
              style={{ marginBottom: "0.5rem" }}
            >
              {updateError}
            </Alert>
          )}
          <Form
            onSubmit={onSubmitEmail}
            initialValues={{
              email: userProfile.email,
            }}
          >
            {(props) => (
              <form onSubmit={props.handleSubmit}>
                <Grid container sx={v4GridContainerSx}>
                  {!duplicateEmailCodeSent && (
                    <Grid size={{ xs: 12, sm: 12 }} sx={inputFieldItemSx}>
                      <Field
                        name="email"
                        validate={composeValidators(isEmailRequired, isEmail)}
                      >
                        {({ input, meta }) => (
                          /* PARITY RESTORE: `variant="standard"` is v4's
                             TextField default. v5+ made "outlined" the default,
                             which would swap the underline input for a boxed one
                             and route the theme's `MuiInput.underline`
                             overrides to a component that is no longer
                             rendered. `InputLabelProps` was removed in the v9
                             line; `slotProps.inputLabel` is the replacement. */
                          <TextField
                            fullWidth
                            variant="standard"
                            required
                            {...input}
                            disabled={updating || !!duplicateEmail}
                            type="email"
                            placeholder="Email"
                            error={meta.error && meta.touched}
                            helperText={meta.error}
                            slotProps={{
                              inputLabel: {
                                shrink: true,
                              },
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                  )}
                  <Grid size={{ xs: 12 }} sx={v4GridItemSx}>
                    {movingError && (
                      <Alert
                        severity="error"
                        variant="filled"
                        style={{ marginBottom: "0.5rem" }}
                      >
                        {movingError}
                      </Alert>
                    )}

                    {updating || moving ? (
                      <Box sx={loadingBoxSx}>
                        <CircularProgress
                          style={{ alignSelf: "center" }}
                          variant="indeterminate"
                        />
                        <Typography
                          variant="body2"
                          style={{ alignSelf: "center" }}
                          sx={bodyTextSmlSx}
                        >
                          {moving ? "Moving" : "Updating"} your Email. Please do
                          not refresh your browser.
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {duplicateEmail && !duplicateEmailCodeSent && (
                          <Alert
                            severity="info"
                            variant="filled"
                            style={{ marginBottom: "0.5rem" }}
                            action={
                              <Button
                                size="small"
                                sx={moveEmailSx}
                                onClick={initialiseMoveEmail}
                              >
                                Move Email
                              </Button>
                            }
                          >
                            We have found this email address registered against
                            another account in our system. You can move this
                            email address into the current account by simply
                            clicking <b>Move Email</b>
                          </Alert>
                        )}

                        {duplicateEmail && duplicateEmailCodeSent && (
                          <Box sx={codeFormBoxSx}>
                            <Typography variant="body2" sx={bodyTextSmlSx}>
                              Please check your email inbox, we have sent you an
                              email containing a 6 digit code which you can
                              input below to confirm your email address.
                              <br />
                              You will not be able to login with this email
                              address until it has been confirmed.
                            </Typography>
                            <CodeForm
                              onSubmit={onSubmitVerificationCode}
                              submitButtonText="Confirm Email Address"
                              descText={undefined}
                              textClassName={CODE_FORM_TEXT_CLASS}
                              containerClassName={CODE_FORM_CONTAINER_CLASS}
                              submitClassName={CODE_FORM_SUBMIT_CLASS}
                            />
                          </Box>
                        )}

                        {!duplicateEmail && (
                          <Box sx={submitRowSx}>
                            <Button
                              sx={submitSx}
                              type="submit"
                              disabled={updating}
                            >
                              Update Email
                            </Button>
                          </Box>
                        )}
                      </>
                    )}
                  </Grid>
                </Grid>
              </form>
            )}
          </Form>
          <Divider sx={footerDividerSx} />
          <Typography variant="body1" sx={bodyTextSmlSx}>
            <span>Tip</span> - This is your email you use to log into your
            FarmFLiX Account.
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
}
